import React, { useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";

const BatchTable = ({
  batches,
  onSort,
  sortField,
  sortDirection,
  onPause,
  onResume,
  onDownload,
  onViewDetails,
}) => {
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRowExpansion = (batchId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded?.has(batchId)) {
      newExpanded?.delete(batchId);
    } else {
      newExpanded?.add(batchId);
    }
    setExpandedRows(newExpanded);
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case "processing":
        return {
          color: "text-accent",
          bgColor: "bg-accent/10",
          icon: "Loader2",
        };
      case "completed":
        return {
          color: "text-success",
          bgColor: "bg-success/10",
          icon: "CheckCircle",
        };
      case "failed":
        return {
          color: "text-error",
          bgColor: "bg-error/10",
          icon: "AlertCircle",
        };
      case "paused":
        return {
          color: "text-warning",
          bgColor: "bg-warning/10",
          icon: "Pause",
        };
      default:
        return {
          color: "text-muted-foreground",
          bgColor: "bg-muted",
          icon: "Circle",
        };
    }
  };

  const handleSort = (field) => {
    onSort(field);
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return "ArrowUpDown";
    return sortDirection === "asc" ? "ArrowUp" : "ArrowDown";
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted border-b border-border">
            <tr>
              <th className="text-left p-4 font-semibold text-foreground">
                <button
                  onClick={() => handleSort("id")}
                  className="flex items-center space-x-1 hover:text-accent transition-colors"
                >
                  <span>Batch ID</span>
                  <Icon name={getSortIcon("id")} size={14} />
                </button>
              </th>
              <th className="text-left p-4 font-semibold text-foreground">
                <button
                  onClick={() => handleSort("uploadTime")}
                  className="flex items-center space-x-1 hover:text-accent transition-colors"
                >
                  <span>Upload Time</span>
                  <Icon name={getSortIcon("uploadTime")} size={14} />
                </button>
              </th>
              <th className="text-left p-4 font-semibold text-foreground">
                <button
                  onClick={() => handleSort("totalCount")}
                  className="flex items-center space-x-1 hover:text-accent transition-colors"
                >
                  <span>Invoice Count</span>
                  <Icon name={getSortIcon("totalCount")} size={14} />
                </button>
              </th>
              <th className="text-left p-4 font-semibold text-foreground">
                <button
                  onClick={() => handleSort("progress")}
                  className="flex items-center space-x-1 hover:text-accent transition-colors"
                >
                  <span>Progress</span>
                  <Icon name={getSortIcon("progress")} size={14} />
                </button>
              </th>
              <th className="text-left p-4 font-semibold text-foreground">
                <button
                  onClick={() => handleSort("status")}
                  className="flex items-center space-x-1 hover:text-accent transition-colors"
                >
                  <span>Status</span>
                  <Icon name={getSortIcon("status")} size={14} />
                </button>
              </th>
              <th className="text-right p-4 font-semibold text-foreground">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {batches?.map((batch) => {
              const statusConfig = getStatusConfig(batch?.status);
              const progressPercentage = Math.round(
                (batch?.processedCount / batch?.totalCount) * 100
              );
              const isExpanded = expandedRows?.has(batch?.id);

              return (
                <React.Fragment key={batch?.id}>
                  <tr className="border-b border-border hover:bg-muted/50 transition-colors">
                    <td className="p-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => toggleRowExpansion(batch?.id)}
                          className="p-1 hover:bg-muted rounded transition-colors"
                        >
                          <Icon
                            name={isExpanded ? "ChevronDown" : "ChevronRight"}
                            size={16}
                            className="text-muted-foreground"
                          />
                        </button>
                        <span className="font-medium text-foreground">
                          {batch?.batchName || `BATCH-${batch?.id}`}
                        </span>
                      </div>
                    </td>
                    <td className="p-4 text-muted-foreground">
                      {batch?.uploadTime}
                    </td>
                    <td className="p-4 text-foreground font-medium">
                      {batch?.totalCount}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center space-x-2">
                        <div className="flex-1 bg-muted rounded-full h-2 min-w-[80px]">
                          <div
                            className={`h-2 rounded-full transition-all duration-300 ${
                              batch?.status === "completed"
                                ? "bg-success"
                                : batch?.status === "failed"
                                ? "bg-error"
                                : batch?.status === "paused"
                                ? "bg-warning"
                                : "bg-accent"
                            }`}
                            style={{ width: `${progressPercentage}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-foreground min-w-[40px]">
                          {progressPercentage}%
                        </span>
                      </div>
                    </td>
                    <td className="p-4">
                      <div
                        className={`
                        inline-flex items-center space-x-1 px-2 py-1 rounded-full text-sm font-medium
                        ${statusConfig?.bgColor} ${statusConfig?.color}
                      `}
                      >
                        <Icon
                          name={statusConfig?.icon}
                          size={12}
                          className={
                            batch?.status === "processing" ? "animate-spin" : ""
                          }
                        />
                        <span>
                          {batch?.status?.charAt(0)?.toUpperCase() +
                            batch?.status?.slice(1)}
                        </span>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-end space-x-1">
                        {batch?.status === "processing" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            iconName="Pause"
                            onClick={() => onPause(batch?.id)}
                            className="h-8 w-8 p-0"
                          />
                        )}

                        {batch?.status === "paused" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            iconName="Play"
                            onClick={() => onResume(batch?.id)}
                            className="h-8 w-8 p-0"
                          />
                        )}

                        {(batch?.status === "completed" ||
                          batch?.status === "failed") && (
                          <Button
                            variant="ghost"
                            size="sm"
                            iconName="Download"
                            onClick={() => onDownload(batch?.id)}
                            className="h-8 w-8 p-0"
                          />
                        )}

                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Eye"
                          onClick={() => onViewDetails(batch?.id)}
                          className="h-8 w-8 p-0"
                        />
                      </div>
                    </td>
                  </tr>
                  {/* Expanded Row Details */}
                  {isExpanded && (
                    <tr>
                      <td colSpan="6" className="p-0">
                        <div className="bg-muted/30 p-4 border-t border-border">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {batch?.invoices?.slice(0, 6)?.map((invoice) => {
                              const invoiceStatusConfig = getStatusConfig(
                                invoice?.status
                              );
                              return (
                                <div
                                  key={invoice?.id}
                                  className="flex items-center space-x-3 p-3 bg-card rounded-lg border border-border"
                                >
                                  <div
                                    className={`
                                    flex items-center justify-center w-8 h-8 rounded-full
                                    ${invoiceStatusConfig?.bgColor}
                                  `}
                                  >
                                    <Icon
                                      name={invoiceStatusConfig?.icon}
                                      size={14}
                                      className={`${
                                        invoiceStatusConfig?.color
                                      } ${
                                        invoice?.status === "processing"
                                          ? "animate-spin"
                                          : ""
                                      }`}
                                    />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-foreground truncate">
                                      {invoice?.filename}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                      {invoice?.status === "processing"
                                        ? invoice?.currentStep
                                        : invoice?.status}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          {batch?.invoices?.length > 6 && (
                            <div className="mt-3 text-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => onViewDetails(batch?.id)}
                              >
                                View all {batch?.invoices?.length} invoices
                              </Button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BatchTable;
